import { TypeChecker } from '../component/prop-types/util';

const VALIDATORS = new Map<string, ValidatorFunction<any>>();
const FORMATTERS = new Map<string, Formatter<any>>();

const DEFAULT: Formatter<any> = {
  format: (value: any) => value,
  parse: (value: any) => value,
  getPattern: () => '',
  validate: () => [{ name: 'default-formatter-validator', valid: true, messages: [] }],
};

interface ValidatorData<T> {
  validator: ValidatorFunction<T>;
  options?: { [k: string]: unknown };
}

/**
 * The result of a validation.
 */
export interface Validation {
  name: string;
  valid: boolean;
  message?: string;
}

interface FormatterFunction<T, FormatterOptions> {
  (value: T, options?: FormatterOptions): string;

  optionTypes?: Record<string, TypeChecker>;

  defaultOptions?: Record<string, any>;
}

export interface ValidatorFunction<T, ValidatorOptions = {}> {
  (value: T, options?: ValidatorOptions): Validation[];

  optionTypes?: Record<string, TypeChecker>;

  defaultOptions?: Record<string, any>;
}

export interface ParserFunction<T, FormatterOptions> {
  (value: string, options?: FormatterOptions): T;
}

export interface Formatter<T, FormatterOptions = {}, ValidatorOptions = {}> {
  format: FormatterFunction<T, FormatterOptions>;

  parse: ParserFunction<T, FormatterOptions>;

  validate: ValidatorFunction<T, ValidatorOptions>;

  getPattern: (options?: FormatterOptions) => string | RegExp;
}

/**
 * Gets a formatter based on the name provided
 *
 * @param name the formatter name
*/
function getFormatter(name: string): Formatter<any> {
  const formatter = FORMATTERS.get(name);

  return formatter || DEFAULT;
}

/**
 * Gets a validator or formatter validator based on the name provided
 *
 * @param name the validator name
*/
function getValidator(name: string): ValidatorFunction<any> {
  const formatter = FORMATTERS.get(name);

  if (formatter) {
    return formatter.validate;
  }

  const validator = VALIDATORS.get(name);

  if (validator) {
    return validator;
  }

  return DEFAULT.validate;
}

/**
 * Gets all formatter names within the catalog
*/
function getFormatters(): string[] {
  return [...FORMATTERS.keys()];
}

/**
 * Gets all validator names within the catalog
 *
 * @param exclusive whether or not to also include formatters
*/
function getValidators(exclusive: boolean): string[] {
  const validators = [...VALIDATORS.keys()];
  return exclusive ? validators : validators.concat(getFormatters());
}

/**
 * Gets the formatting pattern of a formatter
 *
 * @param name the formatter name
*/
function getFormatterPattern(name: string): string {
  const formatter = getFormatter(name);
  return String(formatter.getPattern());
}

/**
 * Formats a value based on the formatter name provided
 *
 * @param name the formatter name
 * @param value the value to be formatted
 * @param options the formatter configuration
*/
function format<T>(name: string, value: T, options?: {}): string {
  const formatter = getFormatter(name);
  return formatter.format(value, options);
}

/**
 * Removes the formatting from a formatted value
 *
 * @param format the formatter name
 * @param value the value to be parsed
*/
function parse<T>(format: string, value: string, options?: {}): T {
  const formatter = getFormatter(format);
  return formatter.parse(value, options);
}

/**
 * Helper class for running multiple validatons for the same value
 *
*/
class ValidationRunner<T> {

  validators: ValidatorData<T>[] = [];

  value: T;

  constructor(value: T) {
    this.value = value;
  }

  /**
   * Adds a new validator to the list
   *
   * @param validatorName the validator name
   * @param options the validator configuration
  */
  apply(validatorName: string, options?: {}): ValidationRunner<T> {
    if (validatorName) {
      const validator = getValidator(validatorName);
      this.validators.push({ validator, options });
    }

    return this;
  }

  /**
   * Executes all validators with the value stored
  */
  run(): Validation[] {
    return this.validators.reduce((all: Validation[], { validator, options }) => {
      return all.concat(validator(this.value, options));
    }, []);
  }
}

/**
 * Creates a ValidatorRunner for the value provided
 *
 * @param value the value to be validated
*/
function validate<T>(value: T): ValidationRunner<T> {
  return new ValidationRunner<T>(value);
}

/**
 * Registers a formatter into the formatters catalog
 *
 * @param name the formatter name
 * @param formatter the formatter object
*/
function registerFormatter<T>(name: string, formatter: Formatter<T>): Formatter<T> {
  if (FORMATTERS.has(name)) {
    throw new Error(`[Model Service] There's already a formatter registered with '${name}' name.`);
  }

  FORMATTERS.set(name, formatter);
  return formatter;
}

/**
 * Registers a validator into the validators catalog
 *
 * @param name the validator name
 * @param validator the validator function
*/
function registerValidator<T>(name: string, validator: ValidatorFunction<T>): ValidatorFunction<T> {
  if (VALIDATORS.has(name)) {
    throw new Error(`[Model Service] There's already a validator registered with '${name}' name.`);
  }

  VALIDATORS.set(name, validator);
  return validator;
}

export {
  format,
  getFormatterPattern,
  getFormatter,
  getFormatters,
  getValidator,
  getValidators,
  parse,
  validate,
  ValidationRunner,
  registerFormatter,
  registerValidator,
};

export default {
  format,
  getFormatterPattern,
  getFormatter,
  getFormatters,
  getValidator,
  getValidators,
  parse,
  validate,
  ValidationRunner,
  registerFormatter,
  registerValidator,
};
